import React, { useState, useEffect, useCallback, useRef } from "react";
import { Upload, Trash2, Search, X, Loader, RefreshCw, Image as ImageIcon, Eye, Plus, Edit2, Tag, Save } from "lucide-react";
import { galleryAPI, galleryCategoriesAPI, uploadAPI } from "../../services/api";
import { getImageUrl } from "../../utils/image";

const MAX_MB = 5;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

// ── Lightbox ─────────────────────────────────────────────
function Lightbox({ item, onClose }) {
  if (!item) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <div className="max-w-3xl w-full" onClick={e => e.stopPropagation()}>
        <img src={getImageUrl(item.imageUrl)} alt={item.altText || item.title} className="w-full max-h-[65vh] object-contain rounded-2xl" />
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl mt-3 p-4">
          <p className="text-white font-semibold text-lg">{item.title}</p>
          {item.description && <p className="text-white/70 text-sm mt-1">{item.description}</p>}
          <div className="flex items-center justify-between mt-2">
            <span className="badge-brand text-xs">{item.category}</span>
            <button onClick={onClose} className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center text-white hover:bg-white/30"><X className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Category Manager ──────────────────────────────────────
function GalleryCategoryManager({ categories, onRefresh }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    try { await galleryCategoriesAPI.create({ name: newName.trim() }); setNewName(""); setAdding(false); onRefresh(); }
    catch (err) { alert(err.response?.data?.error || "Failed to add"); }
    finally { setLoading(false); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    setLoading(true);
    try { await galleryCategoriesAPI.update(id, { name: editName.trim() }); setEditId(null); onRefresh(); }
    catch { alert("Update failed"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try { await galleryCategoriesAPI.delete(id); onRefresh(); } catch { alert("Delete failed"); }
  };

  return (
    <div className="bg-white rounded-2xl shadow-card p-5 mb-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-gray-900">Gallery Categories</h3>
        <button onClick={() => setAdding(!adding)} className="btn-primary text-xs px-3 py-2">
          <Plus className="w-3.5 h-3.5" /> Add Category
        </button>
      </div>
      {adding && (
        <div className="bg-brand-pale rounded-xl p-4 mb-4 space-y-2">
          <input className="input-field text-sm" placeholder="Category name (e.g. Products, Events)" value={newName}
            onChange={e => setNewName(e.target.value)} autoFocus onKeyDown={e => e.key === "Enter" && handleAdd()} />
          <div className="flex gap-2">
            <button onClick={() => { setAdding(false); setNewName(""); }} className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 text-xs">Cancel</button>
            <button onClick={handleAdd} disabled={loading || !newName.trim()} className="flex-1 btn-primary justify-center py-2 text-xs">
              {loading ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5" />Save</>}
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {categories.length === 0 && <p className="text-gray-400 text-sm">No categories yet. Add one above.</p>}
        {categories.map(cat => (
          <div key={cat.id} className="group flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-xl">
            <Tag className="w-3.5 h-3.5 text-brand-primary" />
            {editId === cat.id ? (
              <div className="flex items-center gap-1">
                <input className="input-field text-xs py-1 w-28" value={editName} onChange={e => setEditName(e.target.value)} autoFocus onKeyDown={e => e.key === "Enter" && handleUpdate(cat.id)} />
                <button onClick={() => handleUpdate(cat.id)} className="text-xs text-green-600 font-semibold">{loading ? "..." : "✓"}</button>
                <button onClick={() => setEditId(null)} className="text-xs text-gray-400">✕</button>
              </div>
            ) : (
              <>
                <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                  <button onClick={() => { setEditId(cat.id); setEditName(cat.name); }} className="w-5 h-5 rounded flex items-center justify-center hover:bg-blue-50 hover:text-blue-600 text-gray-300"><Edit2 className="w-3 h-3" /></button>
                  <button onClick={() => handleDelete(cat.id, cat.name)} className="w-5 h-5 rounded flex items-center justify-center hover:bg-red-50 hover:text-red-500 text-gray-300"><Trash2 className="w-3 h-3" /></button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Upload Modal ──────────────────────────────────────────
function UploadModal({ categories, onClose, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState(categories[0]?.name || "General");
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState([]);
  const fileInputRef = useRef();

  const handleFileSelect = e => {
    const selected = Array.from(e.target.files || []);
    const errs = []; const valid = [];
    selected.forEach(f => {
      if (!ALLOWED.includes(f.type)) errs.push(`${f.name}: Only JPEG/PNG/WEBP`);
      else if (f.size > MAX_MB * 1024 * 1024) errs.push(`${f.name}: Max ${MAX_MB}MB`);
      else valid.push({ file: f, title: f.name.replace(/\.[^/.]+$/, ""), description: "", preview: URL.createObjectURL(f) });
    });
    setErrors(errs);
    setFiles(prev => [...prev, ...valid]);
  };

  const handleUpload = async () => {
    if (!files.length) return;
    setUploading(true);
    const uploadErrors = [];
    for (const item of files) {
      try {
        const { data: uploadData } = await uploadAPI.single(item.file, "gallery");
        await galleryAPI.create({ title: item.title || item.file.name, description: item.description || null, category, imageUrl: uploadData.url, altText: item.title, fileSize: item.file.size, mimeType: item.file.type });
      } catch (err) { uploadErrors.push(`${item.title}: ${err.response?.data?.error || "Upload failed"}`); }
    }
    setUploading(false);
    if (uploadErrors.length) { setErrors(uploadErrors); return; }
    setFiles([]); onUploaded(); onClose();
  };

  const removeFile = i => setFiles(prev => { URL.revokeObjectURL(prev[i].preview); return prev.filter((_, idx) => idx !== i); });
  const updateFile = (i, field, val) => setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, [field]: val } : f));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white rounded-t-3xl">
          <h3 className="font-heading font-bold text-gray-900">Upload Gallery Images</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-5">
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              {errors.map((e, i) => <div key={i} className="text-red-700 text-xs">{e}</div>)}
            </div>
          )}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
            <select className="input-field text-sm" value={category} onChange={e => setCategory(e.target.value)}>
              {categories.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
              <option value="General">General</option>
            </select>
          </div>
          <div
            className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center hover:border-brand-primary hover:bg-brand-pale/30 transition-all cursor-pointer"
            onDrop={e => { e.preventDefault(); handleFileSelect({ target: { files: e.dataTransfer.files } }); }}
            onDragOver={e => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-semibold text-sm mb-1">Drop images here or click to browse</p>
            <p className="text-gray-400 text-xs">PNG, JPG, WEBP — Max {MAX_MB}MB — Multiple allowed</p>
            <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFileSelect} />
          </div>

          {files.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold text-gray-600">{files.length} image{files.length !== 1 ? "s" : ""} selected — add title & description for each:</p>
              {files.map((item, i) => (
                <div key={i} className="flex gap-3 bg-gray-50 rounded-2xl p-3 border border-gray-100">
                  <div className="relative flex-shrink-0">
                    <img src={item.preview} alt={item.title} className="w-20 h-20 object-cover rounded-xl" />
                    <button onClick={() => removeFile(i)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white shadow-sm">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex-1 space-y-2">
                    <input className="input-field text-xs py-1.5" placeholder="Image title *" value={item.title}
                      onChange={e => updateFile(i, "title", e.target.value)} />
                    <input className="input-field text-xs py-1.5" placeholder="Short description (optional)"
                      value={item.description} onChange={e => updateFile(i, "description", e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium">Cancel</button>
            {files.length > 0 && (
              <button onClick={handleUpload} disabled={uploading} className="flex-1 btn-primary justify-center py-2.5 text-sm">
                {uploading ? <><Loader className="w-4 h-4 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4" />Upload {files.length} Image{files.length !== 1 ? "s" : ""}</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Edit Modal ────────────────────────────────────────────
function EditModal({ item, categories, onSave, onClose }) {
  const [form, setForm] = useState({ title: item.title, description: item.description || "", category: item.category, altText: item.altText || "" });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try { await galleryAPI.update(item.id, form); onSave(); onClose(); }
    catch { alert("Update failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading font-bold text-gray-900">Edit Image Details</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"><X className="w-4 h-4" /></button>
        </div>
        <img src={getImageUrl(item.imageUrl)} alt="" className="w-full h-40 object-cover rounded-xl mb-4" onError={e => e.target.style.display = "none"} />
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Title</label>
            <input className="input-field text-sm" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Description</label>
            <textarea className="input-field resize-none text-sm" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Short description shown in gallery..." />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Category</label>
            <select className="input-field text-sm" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {categories.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
              <option value="General">General</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Alt Text (for accessibility)</label>
            <input className="input-field text-sm" value={form.altText} onChange={e => setForm(f => ({ ...f, altText: e.target.value }))} placeholder="Describe the image..." />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary justify-center py-2.5 text-sm">
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" />Save Changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Gallery Admin ────────────────────────────────────
export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [viewItem, setViewItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [activeTab, setActiveTab] = useState("images");

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try { const r = await galleryAPI.getAll(); setItems(r.data.items || []); } catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  const fetchCategories = useCallback(async () => {
    try { const r = await galleryCategoriesAPI.getAll(); setCategories(r.data.categories || []); } catch {}
  }, []);

  useEffect(() => { fetchGallery(); fetchCategories(); }, [fetchGallery, fetchCategories]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    try { await galleryAPI.delete(id); setItems(p => p.filter(i => i.id !== id)); } catch {}
  };

  const filtered = items.filter(i => {
    const matchCat = activeCategory === "All" || i.category === activeCategory;
    const matchSearch = !search || i.title.toLowerCase().includes(search.toLowerCase()) || (i.description || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const allCats = ["All", ...categories.map(c => c.name), ...(items.some(i => !categories.find(c => c.name === i.category)) ? ["General"] : [])];
  const uniqueCats = [...new Set(allCats)];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-gray-900 text-xl">Gallery</h2>
          <p className="text-gray-400 text-sm">{items.length} images · {categories.length} categories</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { fetchGallery(); fetchCategories(); }} className="w-9 h-9 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gray-50 shadow-soft"><RefreshCw className="w-4 h-4 text-gray-500" /></button>
          {activeTab === "images" && (
            <button onClick={() => setShowUpload(true)} className="btn-primary text-xs px-4 py-2.5"><Upload className="w-4 h-4" /> Upload Images</button>
          )}
        </div>
      </div>

      {/* Tab Switch */}
      <div className="flex gap-2 border-b border-gray-100 pb-2">
        <button onClick={() => setActiveTab("images")} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${activeTab === "images" ? "bg-brand-pale text-brand-primary" : "text-gray-500 hover:bg-gray-50"}`}>
          <ImageIcon className="w-4 h-4" /> Images
        </button>
        <button onClick={() => setActiveTab("categories")} className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-all ${activeTab === "categories" ? "bg-brand-pale text-brand-primary" : "text-gray-500 hover:bg-gray-50"}`}>
          <Tag className="w-4 h-4" /> Categories
        </button>
      </div>

      {activeTab === "categories" && <GalleryCategoryManager categories={categories} onRefresh={fetchCategories} />}

      {activeTab === "images" && (
        <>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative max-w-xs flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input className="input-field pl-9 py-2 text-sm" placeholder="Search by title or description..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <div className="flex flex-wrap gap-2">
              {uniqueCats.map(cat => (
                <button key={cat} onClick={() => setActiveCategory(cat)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${activeCategory === cat ? "bg-brand-primary text-white" : "bg-gray-100 text-gray-600 hover:bg-brand-pale hover:text-brand-primary"}`}>
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader className="w-6 h-6 text-brand-primary animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
              <ImageIcon className="w-16 h-16 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No images found</p>
              <button onClick={() => setShowUpload(true)} className="mt-4 btn-primary text-xs px-4 py-2"><Upload className="w-3.5 h-3.5" /> Upload First Image</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map(item => (
                <div key={item.id} className="group bg-white rounded-2xl shadow-card hover:shadow-hover transition-all duration-300 overflow-hidden">
                  <div className="relative h-40 bg-brand-pale overflow-hidden">
                    <img src={getImageUrl(item.imageUrl)} alt={item.altText || item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={e => e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><span class="text-gray-300 text-4xl">🖼</span></div>'} />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button onClick={() => setViewItem(item)} className="w-9 h-9 bg-white/90 rounded-xl flex items-center justify-center hover:bg-white" title="View"><Eye className="w-4 h-4 text-gray-700" /></button>
                      <button onClick={() => setEditItem(item)} className="w-9 h-9 bg-white/90 rounded-xl flex items-center justify-center hover:bg-white" title="Edit"><Edit2 className="w-4 h-4 text-gray-700" /></button>
                      <button onClick={() => handleDelete(item.id)} className="w-9 h-9 bg-red-500/90 rounded-xl flex items-center justify-center hover:bg-red-500" title="Delete"><Trash2 className="w-4 h-4 text-white" /></button>
                    </div>
                    <div className="absolute top-2 left-2"><span className="badge-brand text-xs shadow-sm">{item.category}</span></div>
                  </div>
                  <div className="p-3">
                    <p className="font-semibold text-gray-900 text-xs truncate">{item.title}</p>
                    {item.description && <p className="text-gray-400 text-xs truncate mt-0.5">{item.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showUpload && <UploadModal categories={categories} onClose={() => setShowUpload(false)} onUploaded={fetchGallery} />}
      {viewItem && <Lightbox item={viewItem} onClose={() => setViewItem(null)} />}
      {editItem && <EditModal item={editItem} categories={categories} onSave={fetchGallery} onClose={() => setEditItem(null)} />}
    </div>
  );
}
